import { useQueryClient } from '@tanstack/react-query';
import { useDropStatusStream } from '~/hooks/use-drop-status-stream';
import type { DropResponseDto } from '~/lib/api';
import { getDropState } from '~/lib/drop-state';
import { sleep } from '~/lib/sleep';

interface DropCompletedContext {
  invalidateQueries(): void;
}

interface UseDropWithStatusStreamProps {
  drop: DropResponseDto | undefined;
  onDropCompleted?: (context: DropCompletedContext) => void;
}

const DEFAULT_MAX_WAIT_TIME = 10e3;

export const useDropWithStatusStream = ({ drop, onDropCompleted }: UseDropWithStatusStreamProps) => {
  const queryClient = useQueryClient();

  const dropState = drop ? getDropState(drop) : null;

  const { status, isConnected, error, disconnect } = useDropStatusStream({
    dropId: drop?.id ?? '',
    enabled: Boolean(drop) && (dropState?.type === 'processing' || dropState?.type === 'ending-soon'),
    onDropCompleted: () => {
      onDropCompleted?.({
        invalidateQueries() {
          const waitTime = Math.random() * DEFAULT_MAX_WAIT_TIME;

          void sleep(waitTime).then(() => {
            void queryClient.invalidateQueries();
          });
        },
      });
    },
  });

  const hasEnded = dropState?.type === 'completed' || dropState?.type === 'processing';

  return {
    hasEnded,
    dropState,
    status,
    isConnected,
    error,
    disconnect,
  };
};
